import { t as uiText } from '../i18n';
import React, { useMemo, useState } from 'react';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { parseCsvRecords } from '../utils/csvParse';
import { decodeCsvFile } from '../utils/decodeCsvFile';
import { parseImportTags } from '../utils/importTags';
import { generateEmbeddingsForBoards } from '../utils/generateEmbeddingsForBoards';

const TAG_HEADER_NAMES = ['タグ', 'tags', 'tag', 'ハッシュタグ', 'hashtags'];

interface ImportPagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  /** 取り込み完了ダイアログの OK のあと。一覧の再取得に使う。 */
  onImported?: () => void;
}

const ImportPagesModal: React.FC<ImportPagesModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  onImported,
}) => {
  if (!isOpen) return null;

  const [rawCsvName, setRawCsvName] = useState<string>('');
  const [rawCsvText, setRawCsvText] = useState<string>('');
  const [rows, setRows] = useState<string[][]>([]);
  const [hasHeader, setHasHeader] = useState<boolean>(true);
  const [titleCol, setTitleCol] = useState<number>(0);
  const [contentCol, setContentCol] = useState<number>(1);
  /** -1 = タグ列なし */
  const [tagsCol, setTagsCol] = useState<number>(-1);
  const [delimiter, setDelimiter] = useState<string>(',');
  const [duplicateHandling, setDuplicateHandling] = useState<'skip' | 'overwrite'>('skip');
  const [checkingDup, setCheckingDup] = useState<boolean>(false);
  const [duplicateMap, setDuplicateMap] = useState<Record<number, { exists: boolean; boardId?: string }>>({});
  const [error, setError] = useState<string>('');
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [progressText, setProgressText] = useState<string>('');
  const [importSummary, setImportSummary] = useState<null | {
    createdCount: number;
    updatedCount: number;
    skippedCount: number;
    failedCount: number;
  }>(null);

  const header = useMemo(() => {
    if (!rows.length) return [];
    return hasHeader ? rows[0] : rows[0].map((_, i) => uiText("Column {{v0}}", { v0: i + 1 }));
  }, [rows, hasHeader]);

  const dataRows = useMemo(() => {
    if (!rows.length) return [];
    const base = hasHeader ? rows.slice(1) : rows;
    // タイトル・コンテンツともに空の行は除外
    return base.filter((r) => {
      const t = ((r?.[titleCol] ?? '') as string).trim();
      const c = ((r?.[contentCol] ?? '') as string).trim();
      return !!(t || c);
    });
  }, [rows, hasHeader, titleCol, contentCol]);

  const guessColumns = (hdr: string[]) => {
    const lower = hdr.map(h => h.trim().toLowerCase());
    const tIdx = lower.findIndex(h => [uiText("Title"), 'title'].includes(h));
    const cIdx = lower.findIndex(h => [uiText("Content"), uiText("Contents"), 'content', 'body'].includes(h));
    setTitleCol(tIdx >= 0 ? tIdx : 0);
    setContentCol(cIdx >= 0 ? cIdx : Math.min(1, hdr.length - 1));
    setTagsCol(lower.findIndex(h => TAG_HEADER_NAMES.includes(h)));
  };

  const handleCsvSelect = async (file: File) => {
    try {
      setError('');
      setDuplicateMap({});
      setCheckingDup(false);
      setRawCsvName(file.name);
      const text = await decodeCsvFile(file);
      setRawCsvText(text);
      const parsed = parseCsvRecords(text, delimiter);
      setRows(parsed);
      if (parsed.length > 0) {
        const hdr = hasHeader ? parsed[0] : parsed[0].map((_, i) => uiText("Column {{v0}}", { v0: i + 1 }));
        // 列推測をローカルで行い、その列で即時重複チェックを実行
        const lower = hdr.map(h => h.trim().toLowerCase());
        const tIdx = lower.findIndex(h => [uiText("Title"), 'title'].includes(h));
        const cIdx = lower.findIndex(h => [uiText("Content"), uiText("Contents"), 'content', 'body'].includes(h));
        const decidedTitleIdx = tIdx >= 0 ? tIdx : 0;
        const decidedContentIdx = cIdx >= 0 ? cIdx : Math.min(1, hdr.length - 1);
        setTitleCol(decidedTitleIdx);
        setContentCol(decidedContentIdx);
        setTagsCol(lower.findIndex(h => TAG_HEADER_NAMES.includes(h)));
        // アップロード直後に重複チェック（プレビューと同じフィルタで）
        await (async () => {
          try {
            await checkDuplicatesWith(parsed, hasHeader, decidedTitleIdx, decidedContentIdx);
          } catch {}
        })();
      }
    } catch (e: any) {
      setError(e?.message || uiText("Failed to load the CSV"));
    }
  };

  const handleReparse = () => {
    try {
      setError('');
      setDuplicateMap({});
      setCheckingDup(false);
      if (!rawCsvText) return;
      const parsed = parseCsvRecords(rawCsvText, delimiter);
      setRows(parsed);
      if (parsed.length > 0) {
        const hdr = hasHeader ? parsed[0] : parsed[0].map((_, i) => uiText("Column {{v0}}", { v0: i + 1 }));
        guessColumns(hdr);
      }
    } catch (e: any) {
      setError(e?.message || uiText("Reanalysis failed"));
    }
  };

  // Firestore上でタイトル一致（editData.title / title）をバッチ照合（in句最大10ずつ）
  const checkDuplicates = async () => {
    if (!workspaceId || !dataRows.length) return;
    setCheckingDup(true);
    setDuplicateMap({});
    try {
      const db = getFirestore();
      const titles = Array.from(new Set(dataRows.map(r => (r?.[titleCol] || '').trim()).filter(Boolean)));
      const titleToBoardId = new Map<string, string>();

      // まず editData.title で照合（10件ずつ）
      for (let i = 0; i < titles.length; i += 10) {
        const chunk = titles.slice(i, i + 10);
        const q1 = query(collection(db, `workspaces/${workspaceId}/boards`), where('editData.title', 'in', chunk));
        const snap1 = await getDocs(q1);
        snap1.forEach(d => {
          const t = (d.get('editData.title') || '').toString().trim();
          if (t) titleToBoardId.set(t, d.id);
        });
      }

      // 残りは title フィールドでフォールバック照合
      const remaining = titles.filter(t => !titleToBoardId.has(t));
      for (let i = 0; i < remaining.length; i += 10) {
        const chunk = remaining.slice(i, i + 10);
        const q2 = query(collection(db, `workspaces/${workspaceId}/boards`), where('title', 'in', chunk));
        const snap2 = await getDocs(q2);
        snap2.forEach(d => {
          const t = (d.get('title') || '').toString().trim();
          if (t && !titleToBoardId.has(t)) titleToBoardId.set(t, d.id);
        });
      }

      const results: Record<number, { exists: boolean; boardId?: string }> = {};
      dataRows.forEach((r, idx) => {
        const t = (r?.[titleCol] || '').trim();
        if (t && titleToBoardId.has(t)) {
          results[idx] = { exists: true, boardId: titleToBoardId.get(t) };
        } else {
          results[idx] = { exists: false };
        }
      });
      setDuplicateMap(results);
    } catch (e: any) {
      setError(e?.message || uiText("An error occurred during the duplicate check"));
    } finally {
      setCheckingDup(false);
    }
  };

  // アップロード直後に、任意の列設定で実行できる重複チェック（全件）
  const checkDuplicatesWith = async (rowsArg: string[][], hasHeaderArg: boolean, titleColArg: number, contentColArg?: number) => {
    if (!workspaceId || !rowsArg.length) return;
    setCheckingDup(true);
    setDuplicateMap({});
    try {
      const db = getFirestore();
      const targetBase = hasHeaderArg ? rowsArg.slice(1) : rowsArg;
      const cIdx = typeof contentColArg === 'number' ? contentColArg : contentCol;
      // タイトル・コンテンツともに空の行は除外（プレビューと一致）
      const target = targetBase.filter((r) => {
        const t = ((r?.[titleColArg] ?? '') as string).trim();
        const c = ((r?.[cIdx] ?? '') as string).trim();
        return !!(t || c);
      });
      const titles = Array.from(new Set(target.map(r => (r?.[titleColArg] || '').trim()).filter(Boolean)));
      const titleToBoardId = new Map<string, string>();

      for (let i = 0; i < titles.length; i += 10) {
        const chunk = titles.slice(i, i + 10);
        const q1 = query(collection(db, `workspaces/${workspaceId}/boards`), where('editData.title', 'in', chunk));
        const snap1 = await getDocs(q1);
        snap1.forEach(d => {
          const t = (d.get('editData.title') || '').toString().trim();
          if (t) titleToBoardId.set(t, d.id);
        });
      }
      const remaining = titles.filter(t => !titleToBoardId.has(t));
      for (let i = 0; i < remaining.length; i += 10) {
        const chunk = remaining.slice(i, i + 10);
        const q2 = query(collection(db, `workspaces/${workspaceId}/boards`), where('title', 'in', chunk));
        const snap2 = await getDocs(q2);
        snap2.forEach(d => {
          const t = (d.get('title') || '').toString().trim();
          if (t && !titleToBoardId.has(t)) titleToBoardId.set(t, d.id);
        });
      }

      const results: Record<number, { exists: boolean; boardId?: string }> = {};
      target.forEach((r, idx) => {
        const t = (r?.[titleColArg] || '').trim();
        if (t && titleToBoardId.has(t)) {
          results[idx] = { exists: true, boardId: titleToBoardId.get(t) };
        } else {
          results[idx] = { exists: false };
        }
      });
      setDuplicateMap(results);
    } catch (e: any) {
      setError(e?.message || uiText("An error occurred during the duplicate check"));
    } finally {
      setCheckingDup(false);
    }
  };

  // CSVテンプレートをダウンロードする関数
  const handleDownloadTemplate = () => {
    // CSVの内容を生成（BOM付きUTF-8でExcel対応）
    const csvContent = '\uFEFF' + uiText("Title,Content,Tags\n");
    
    // Blobを作成
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // ダウンロードリンクを作成
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'notreu-import-template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={() => { if (!isImporting) onClose(); }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          padding: '32px',
          borderRadius: '8px',
          minWidth: '500px',
          maxWidth: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0, marginBottom: '20px' }}>{uiText("Upload page")}</h2>
        
        {/* テンプレートダウンロードセクション */}
        <div style={{ 
          marginBottom: '24px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            marginTop: 0, 
            marginBottom: '12px',
            color: '#333'
          }}>
            {uiText("1. Download the template")}
          </h3>
          <p style={{ 
            fontSize: '14px', 
            color: '#666', 
            marginBottom: '16px',
            lineHeight: '1.6'
          }}>
            {uiText("Download the CSV template and enter titles and content.")}
          </p>
          <p style={{
            fontSize: '13px',
            color: '#666',
            marginTop: '-8px',
            marginBottom: '16px',
            lineHeight: '1.6'
          }}>
            {uiText("To add tags, enter them in the Tags column. Separate multiple tags with commas or spaces. The # is optional.")}
          </p>
          <button
            onClick={handleDownloadTemplate}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--workspace-primary, #cfae91)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--workspace-primary-dark, #b89a7a)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
            }}
          >
            {uiText("Download template")}
          </button>
        </div>

        {/* CSVアップロード */}
        <div style={{ 
          padding: '40px', 
          textAlign: 'center', 
          color: '#666',
          border: '2px dashed #ddd',
          borderRadius: '8px'
        }}>
          <div style={{ marginBottom: '12px', fontWeight: 600 }}>{uiText("2. Upload a CSV file")}</div>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleCsvSelect(f);
            }}
          />
          {!!rawCsvName && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#333' }}>
              {uiText("Now selected:")} {rawCsvName}
            </div>
          )}
          {/* 解析オプション */}
          <div style={{ marginTop: '16px', display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
              {uiText("Delimiter:")}
              <select value={delimiter} onChange={(e) => setDelimiter(e.target.value)} style={{ padding: '4px 8px' }}>
                <option value=",">{uiText(", (comma)")}</option>
                <option value=";">{uiText("; (semicolon)")}</option>
                <option value="\t">{uiText("Tab")}</option>
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
              {uiText("First row is a header")}
              <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
            </label>
            <button
              onClick={handleReparse}
              disabled={!rawCsvText}
              style={{
                padding: '6px 12px',
                backgroundColor: rawCsvText ? '#6c757d' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: rawCsvText ? 'pointer' : 'not-allowed',
                fontSize: '13px'
              }}
            >
              {uiText("Reanalyze")}
            </button>
          </div>
        </div>

        {/* 列マッピング & プレビュー */}
        {!!rows.length && (
          <div style={{ marginTop: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: 0, marginBottom: '12px', color: '#333' }}>
              {uiText("3. Map columns")}
            </h3>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                {uiText("Title column")}
                <select value={titleCol} onChange={(e) => setTitleCol(Number(e.target.value))} style={{ padding: '4px 8px' }}>
                  {header.map((h, idx) => (
                    <option key={idx} value={idx}>{`${idx + 1}: ${h}`}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                {uiText("Content column")}
                <select value={contentCol} onChange={(e) => setContentCol(Number(e.target.value))} style={{ padding: '4px 8px' }}>
                  {header.map((h, idx) => (
                    <option key={idx} value={idx}>{`${idx + 1}: ${h}`}</option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                {uiText("Tags column")}
                <select value={tagsCol} onChange={(e) => setTagsCol(Number(e.target.value))} style={{ padding: '4px 8px' }}>
                  <option value={-1}>{uiText("None")}</option>
                  {header.map((h, idx) => (
                    <option key={idx} value={idx}>{`${idx + 1}: ${h}`}</option>
                  ))}
                </select>
              </label>
            </div>

            {/* 重複チェック */}
            <div style={{ marginTop: '16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={checkDuplicates}
                disabled={checkingDup || !dataRows.length}
                style={{
                  padding: '8px 12px',
                  backgroundColor: checkingDup ? '#999' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: checkingDup ? 'wait' : 'pointer',
                  fontSize: '13px'
                }}
              >
                {checkingDup ? uiText("Checking for duplicates...") : uiText("Duplicate check")}
              </button>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                {uiText("When duplicates are found")}
                <select value={duplicateHandling} onChange={(e) => setDuplicateHandling(e.target.value as any)} style={{ padding: '4px 8px' }}>
                  <option value="skip">{uiText("Skip")}</option>
                  <option value="overwrite">{uiText("Overwrite")}</option>
                </select>
              </label>
            </div>

            {/* プレビュー表 */}
            <div style={{ marginTop: '12px', border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ maxHeight: '280px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#fafafa' }}>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee', width: '36px' }}>#</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee' }}>{uiText("Title")}</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee' }}>{uiText("Content")}</th>
                      {tagsCol >= 0 && (
                        <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee', width: '140px' }}>{uiText("Tags")}</th>
                      )}
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #eee', width: '120px' }}>{uiText("Duplicate")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataRows.slice(0, 50).map((r, idx) => {
                      const t = r[titleCol] || '';
                      const c = r[contentCol] || '';
                      const dup = duplicateMap[idx];
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid #f3f3f3' }}>
                          <td style={{ padding: '8px', color: '#666' }}>{idx + 1}</td>
                          <td style={{ padding: '8px', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}>{t}</td>
                          <td style={{ padding: '8px', color: '#444', whiteSpace: 'pre-wrap', maxWidth: 320 }}>{c}</td>
                          {tagsCol >= 0 && (
                            <td style={{ padding: '8px', color: '#444', maxWidth: 160 }}>
                              {parseImportTags(r[tagsCol]).map((tag) => `#${tag}`).join(' ')}
                            </td>
                          )}
                          <td style={{ padding: '8px' }}>
                            {!dup ? (
                              <span style={{ color: '#999' }}>{uiText("Unchecked")}</span>
                            ) : dup.exists ? (
                              <span style={{ color: '#d32f2f' }}>{uiText("Duplicate")} {dup.boardId ? `(${dup.boardId})` : ''}</span>
                            ) : (
                              <span style={{ color: '#2e7d32' }}>{uiText("None")}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '8px 12px', fontSize: '12px', color: '#666', backgroundColor: '#fafafa', borderTop: '1px solid #eee' }}>
                {uiText("Showing the first 50 items (of ")}{dataRows.length}{uiText(" item)")}
              </div>
            </div>
          </div>
        )}

        {!!error && (
          <div style={{ marginTop: '12px', color: '#d32f2f', fontSize: '12px' }}>
            {error}
          </div>
        )}

        <div style={{ marginTop: '24px', textAlign: 'right', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={isImporting}
            style={{
              padding: '8px 16px',
              backgroundColor: '#ccc',
              color: '#000',
              border: 'none',
              borderRadius: '4px',
              cursor: isImporting ? 'not-allowed' : 'pointer',
              opacity: isImporting ? 0.5 : 1,
            }}
          >
            {uiText("Cancel")}
          </button>
          <button
            disabled={!rows.length || isImporting}
            title={!rows.length ? uiText("Please load a CSV") : ''}
            style={{
              padding: '8px 16px',
              backgroundColor: rows.length && !isImporting ? 'var(--workspace-primary, #cfae91)' : '#e0e0e0',
              color: rows.length && !isImporting ? '#fff' : '#999',
              border: 'none',
              borderRadius: '4px',
              cursor: rows.length && !isImporting ? 'pointer' : 'not-allowed',
            }}
            onClick={async () => {
              if (!rawCsvText || !rows.length) return;
              try {
                setError('');
                setImportSummary(null);
                setIsImporting(true);
                setProgressText(uiText("Converting CSV..."));
                // 件数が多いと数分かかる。Firebase の既定 70 秒で切れないよう、サーバー上限に合わせる
                const processCsv = httpsCallable(functions, 'processCsvToNdjsonFn', { timeout: 120_000 });
                const res1: any = await processCsv({
                  workspaceId,
                  csvText: rawCsvText,
                  hasHeader,
                  delimiter,
                  titleIndex: titleCol,
                  contentIndex: contentCol,
                  tagsIndex: tagsCol,
                });
                const ndjsonGcsPath = res1?.data?.ndjsonGcsPath;
                const totalRecords = res1?.data?.totalRecords ?? 0;
                if (!ndjsonGcsPath) {
                  throw new Error(uiText("Failed to generate NDJSON"));
                }

                setProgressText(uiText("Importing... ({{totalRecords}} items)", { totalRecords: totalRecords }));
                const importFn = httpsCallable(functions, 'importNdjsonToFirestoreFn', { timeout: 540_000 });
                const res2: any = await importFn({
                  workspaceId,
                  ndjsonGcsPath,
                  folderId: null,
                  duplicateHandling,
                });

                const created: Array<{ boardId: string }> = res2?.data?.created || [];
                const updated: Array<{ boardId: string }> = res2?.data?.updated || [];
                const skippedCount: number = res2?.data?.skippedCount || 0;
                const failed: Array<any> = res2?.data?.failed || [];

                const targets = [...created, ...updated];

                const summary = {
                  createdCount: res2?.data?.createdCount || 0,
                  updatedCount: res2?.data?.updatedCount || 0,
                  skippedCount,
                  failedCount: res2?.data?.failedCount || 0,
                };
                setImportSummary(summary);

                // AI 検索用のデータ作成。途中で閉じると未完了になるため、このまま完了まで待つ
                // （知識軸が ON のページは公開 AI 用にも自動で反映される）
                let embeddingFailed = 0;
                if (targets.length > 0) {
                  const { failedBoardIds } = await generateEmbeddingsForBoards(
                    workspaceId,
                    targets.map((t) => t.boardId),
                    (p) => setProgressText(
                      uiText("Preparing for AI search... ({{done}}/{{total}}) Please keep this window open.", { done: p.done, total: p.total })
                    ),
                  );
                  embeddingFailed = failedBoardIds.length;
                }

                setProgressText(uiText("Completed"));
                setIsImporting(false);

                alert(
                  uiText("Import complete\n") +
                  uiText("Created: {{createdCount}}\n", { createdCount: summary.createdCount }) +
                  uiText("Overwritten: {{updatedCount}}\n", { updatedCount: summary.updatedCount }) +
                  uiText("Skipped: {{skippedCount}}\n", { skippedCount: summary.skippedCount }) +
                  uiText("Failed: {{failedCount}}", { failedCount: summary.failedCount }) +
                  (embeddingFailed > 0
                    ? "\n" + uiText("{{count}} items could not be prepared for AI search. The pages were created; open them and save again to retry.", { count: embeddingFailed })
                    : '')
                );
                onImported?.();
                onClose();
              } catch (e: any) {
                setError(e?.message || uiText("An error occurred during import"));
                setIsImporting(false);
              } finally {
                setProgressText('');
              }
            }}
          >
            {isImporting ? uiText("Processing...") : uiText("Run import")}
          </button>
        </div>
        {(isImporting || importSummary) && (
          <div style={{ marginTop: '12px', textAlign: 'right', fontSize: '12px', color: '#555' }}>
            {progressText}
            {importSummary && !isImporting && (
              <div style={{ marginTop: '6px', color: '#333' }}>
                {uiText("New")} {importSummary.createdCount} {uiText("/ Overwrite")} {importSummary.updatedCount} {uiText("/ Skipped")} {importSummary.skippedCount} {uiText("/ Failed")} {importSummary.failedCount}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportPagesModal;

